import { useEffect, useState } from "react";
import api, { money } from "../../lib/api";
import { PageHeader, StatCard } from "../../components/PortalShell";
import { BarChart } from "../../components/Sparkline";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../../components/ui/dropdown-menu";
import { Download, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const EXPORT_KINDS = [
  { k: "invoices", l: "Invoices · per-row", desc: "Every invoice with status, supplier, due/paid dates" },
  { k: "orders",   l: "Order line items",   desc: "Pivot-friendly: one row per SKU per order" },
  { k: "summary",  l: "Supplier summary",   desc: "Aggregated totals, paid count, overdue count" },
];

export default function BuyerReports() {
  const [r, setR] = useState(null);
  const [trend, setTrend] = useState(null);
  const [exporting, setExporting] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/reports/summary"), api.get("/spend/trend")]).then(([a, b]) => { setR(a.data); setTrend(b.data); });
  }, []);

  const exportCsv = async (kind) => {
    setExporting(kind);
    try {
      const res = await api.get(`/buyer/reports/export?kind=${kind}`, { responseType: "blob" });
      // Pull filename from Content-Disposition; fallback to a sane default.
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const filename = m?.[1] || `buyer-${kind}-${new Date().toISOString().slice(0,10)}.csv`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally { setExporting(null); }
  };

  if (!r) return <div className="p-8 text-foreground/60">Loading…</div>;

  return (
    <>
      <PageHeader title="Reports & Analytics" subtitle="Spend, margin, supplier performance"
        action={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="reports-export-btn" disabled={!!exporting}>
                <Download className="w-4 h-4 mr-1"/>{exporting ? "Exporting…" : "Export CSV"}<ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-60"/>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              {EXPORT_KINDS.map(opt => (
                <DropdownMenuItem key={opt.k} onSelect={() => exportCsv(opt.k)} className="flex flex-col items-start py-2.5" data-testid={`reports-export-${opt.k}`}>
                  <span className="text-sm font-medium">{opt.l}</span>
                  <span className="text-[11px] text-foreground/55 mt-0.5">{opt.desc}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        }/>
      <div className="p-8 space-y-6">
        <div className="grid md:grid-cols-4 gap-4">
          <StatCard label="Total spend" value={money(r.total_spend)} hint={`${r.invoices_count} invoices`} tone="ink"/>
          <StatCard label="Paid" value={money(r.paid)} hint={`${r.total_spend ? Math.round((r.paid/r.total_spend)*100) : 0}% paid rate`}/>
          <StatCard label="Overdue" value={money(r.overdue)} hint="needs attention"/>
          <StatCard label="Avg order" value={money(r.avg_order)} hint={`${r.orders_count} orders`}/>
        </div>

        <div className="rounded-2xl bg-white border border-border p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="display text-lg">Spend trend</h2>
              <p className="text-xs text-foreground/60">Last 6 months</p>
            </div>
          </div>
          {trend?.months && <div className="h-56"><BarChart data={trend.months}/></div>}
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl bg-white border border-border p-6">
            <h2 className="display text-lg mb-4">Top suppliers by spend</h2>
            <div className="space-y-3">
              {r.by_seller.slice(0, 6).map((s, i) => {
                const max = r.by_seller[0]?.value || 1;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1"><span>{s.name}</span><span className="display">{money(s.value)}</span></div>
                    <div className="h-1.5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-foreground" style={{width: `${(s.value/max)*100}%`}}/></div>
                  </div>
                );
              })}
              {r.by_seller.length === 0 && <div className="py-4 text-center text-foreground/60 text-sm">No data</div>}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-border p-6">
            <h2 className="display text-lg mb-4">Spend by category</h2>
            <div className="space-y-3">
              {r.by_category.map((c, i) => {
                const max = r.by_category[0]?.value || 1;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1"><span className="capitalize">{c.name}</span><span className="display">{money(c.value)}</span></div>
                    <div className="h-1.5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-accent" style={{width: `${(c.value/max)*100}%`}}/></div>
                  </div>
                );
              })}
              {r.by_category.length === 0 && <div className="py-4 text-center text-foreground/60 text-sm">No data</div>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
